import { inject, Injectable } from '@angular/core';
import { CartItem } from './cart.model';
import { BehaviorSubject, first, Observable } from 'rxjs';
import { Property } from '../properties/properties.model';
import { dummyDataProperty } from '@shared/dummyData/property-405';
import { COLLECTION, db } from '@shared/helpers/database';
import { HelpersService } from '@shared/services/helpers.service';
import { Permission, Query } from 'appwrite';
import { Role } from 'appwrite';
import { AuthService } from '@shared/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  readonly cart$: Observable<CartItem[]>;
  readonly investedItems$: Observable<CartItem[]>;
  readonly isCartLoading$: Observable<boolean>;

  private readonly _cart$ = new BehaviorSubject<CartItem[]>([]);
  private readonly _investedItems$ = new BehaviorSubject<CartItem[]>([]);
  private readonly _isCartLoading$ = new BehaviorSubject<boolean>(false);

  private readonly _helperService = inject(HelpersService);
  private readonly _authService = inject(AuthService);

  constructor() {
    this.cart$ = this._cart$.asObservable();
    this.investedItems$ = this._investedItems$.asObservable();
    this.isCartLoading$ = this._isCartLoading$.asObservable();
  }

  addToCart(property: Property, amount: number): void {
    this._authService.user$.pipe(first()).subscribe((user) => {
      db[COLLECTION.CART]
        .list([Query.equal('property', [property.$id])])
        .then((res: any) => {
          // If the property is already in the cart, update the amount
          if (res.documents.length > 0) {
            this.updateCartItem(res.documents[0], property, amount);
          } else {
            const role = Role.user(user!.$id);

            const item: any = {
              property: property.$id,
              amount,
              monthlyCashflow: this.calculateMonthlyCashflow(property, amount),
            };

            db[COLLECTION.CART]
              .create(item, [
                Permission.read(role),
                Permission.update(role),
                Permission.delete(role),
              ])
              .then((res: any) => {
                const cartItem: CartItem = {
                  ...res,
                  property: property,
                };

                this._cart$.next([...this._cart$.value, cartItem]);
              });
          }
        });
    });
  }

  updateCartItem(cartItem: CartItem, property: Property, amount: number): void {
    const item: any = {
      $id: cartItem.$id,
      property: property.$id,
      amount: cartItem.amount + amount,
      monthlyCashflow: this.calculateMonthlyCashflow(
        property,
        cartItem.amount + amount
      ),
    };

    db[COLLECTION.CART].update(item.$id, item).then((res: any) => {
      const cartItem: CartItem = {
        ...res,
        property: property,
      };

      const cartItems = this._cart$.getValue();
      cartItems.find((item) => item.$id === cartItem.$id)!.amount = item.amount;

      this._cart$.next([...cartItems]);
    });
  }

  removeFromCart(index: number): void {
    const cart = this._cart$.value;
    const cartItem: CartItem = cart.splice(index, 1)[0];

    db[COLLECTION.CART].delete(cartItem.$id).then((res: any) => {
      this._cart$.next(cart);
    });
  }

  clearCart(): void {
    this._cart$.value.forEach((cartItem) => {
      db[COLLECTION.CART].delete(cartItem.$id);
    });

    this._cart$.next([]);
  }

  setInvestedItems(investedItems: CartItem[]): void {
    this._investedItems$.next(investedItems);
  }

  clearInvestedCart(): void {
    this._investedItems$.next([]);
  }

  getCart(): void {
    this._isCartLoading$.next(true);

    db[COLLECTION.CART].list().then((cartItems: any) => {
      const cart: CartItem[] = cartItems.documents.map((cartItem: any) => {
        cartItem.property.images = cartItem.property.images?.map(
          (image: string) => {
            const tmpImage = {
              id: image,
              url: this._helperService.getSafeImageUrl(image, 128, 80),
            };

            return tmpImage;
          }
        );

        return cartItem;
      });

      this._cart$.next(cart);

      this._isCartLoading$.next(false);
    });
  }

  calculateMonthlyCashflow(property: Property, amount: number): number {
    const totalMonthlyRent = (property.annualRent || 0) / 12;
    const ownershipRatio = amount / property.price;
    const myMonthlyRent = totalMonthlyRent * ownershipRatio;

    return myMonthlyRent;
  }
}
